// src\components\ConfigurationView.jsx
import React, { useState } from 'react';
import { Plus, Edit, Save, Trash2, X, Check, Clock, Settings, ArrowLeft, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

const ConfigurationView = () => {
  const [activeTab, setActiveTab] = useState('cycles');
  const [editingCycle, setEditingCycle] = useState(null);
  const [editingResource, setEditingResource] = useState(null);
  const [showCycleModal, setShowCycleModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  
  // Données simulées
  const cycles = [
    { 
      id: 1, 
      name: 'A42', 
      code: 'Cycle 180°C',
      duration: 300, // minutes
      loadingTime: 30,
      unloadingTime: 20,
      compatibleResources: [1, 2],
      temp: 180,
      steps: [
        { name: 'Montée en température', duration: 45, targetTemp: 180 },
        { name: 'Maintien', duration: 180, targetTemp: 180 },
        { name: 'Refroidissement', duration: 75, targetTemp: 60 }
      ]
    },
    { 
      id: 2, 
      name: 'A35', 
      code: 'Cycle 160°C',
      duration: 270, 
      loadingTime: 30,
      unloadingTime: 20,
      compatibleResources: [1, 2, 3, 4],
      temp: 160,
      steps: [
        { name: 'Montée en température', duration: 40, targetTemp: 160 },
        { name: 'Maintien', duration: 160, targetTemp: 160 },
        { name: 'Refroidissement', duration: 70, targetTemp: 60 }
      ]
    },
    { 
      id: 3, 
      name: 'E25', 
      code: 'Cycle  120°C',
      duration: 480, 
      loadingTime: 45,
      unloadingTime: 30,
      compatibleResources: [3, 4],
      temp: 120,
      steps: [
        { name: 'Montée en température', duration: 60, targetTemp: 120 },
        { name: 'Maintien', duration: 360, targetTemp: 120 },
        { name: 'Refroidissement', duration: 60, targetTemp: 60 }
      ]
    },
    { 
      id: 4, 
      name: 'A29', 
      code: 'Cycle  200°C',
      duration: 180, 
      loadingTime: 25,
      unloadingTime: 15,
      compatibleResources: [1, 2],
      temp: 200,
      steps: [
        { name: 'Montée en température', duration: 50, targetTemp: 200 },
        { name: 'Maintien', duration: 90, targetTemp: 200 },
        { name: 'Refroidissement', duration: 40, targetTemp: 60 }
      ]
    },
    { 
      id: 5, 
      name: 'E28', 
      code: 'Cycle Spécial 140°C',
      duration: 360, 
      loadingTime: 35,
      unloadingTime: 25,
      compatibleResources: [3, 4],
      temp: 140,
      steps: [
        { name: 'Montée en température', duration: 55, targetTemp: 140 },
        { name: 'Maintien', duration: 240, targetTemp: 140 },
        { name: 'Refroidissement', duration: 65, targetTemp: 60 }
      ]
    },
  ];
  
  const resources = [
    { id: 1, name: 'Autoclave 1', type: 'autoclave', capacity: '10 OT', maxTemp: 200, maxVacuumPorts: 8, maxTc: 12 },
    { id: 2, name: 'Autoclave 2', type: 'autoclave', capacity: '15 OT', maxTemp: 220, maxVacuumPorts: 16, maxTc: 24 },
    { id: 3, name: 'Étuve 1', type: 'etuve', capacity: '10 OT', maxTemp: 200, maxVacuumPorts: 2, maxTc: 6 },
    { id: 4, name: 'Étuve 2', type: 'etuve', capacity: '15 OT', maxTemp: 250, maxVacuumPorts: 4, maxTc: 6 },
  ];
  
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };
  
  const CyclesTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-gray-800">Configuration des Cycles de Polymérisation</h1>
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
          onClick={() => {
            setEditingCycle({
              id: Math.max(...cycles.map(c => c.id)) + 1,
              name: 'Nouveau Cycle',
              code: 'Description du cycle',
              duration: 240,
              loadingTime: 30,
              unloadingTime: 20,
              compatibleResources: [],
              temp: 160,
              steps: [
                { name: 'Montée en température', duration: 40, targetTemp: 160 },
                { name: 'Maintien', duration: 160, targetTemp: 160 },
                { name: 'Refroidissement', duration: 40, targetTemp: 60 }
              ]
            });
            setShowCycleModal(true);
          }}
        >
          <Plus size={18} className="mr-2" />
          Nouveau Cycle
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code Cycle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Température</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durée Totale</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temps Chargement</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Temps Déchargement</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Moyens Compatibles</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {cycles.map((cycle) => (
              <tr key={cycle.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{cycle.name}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cycle.code}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{cycle.temp}°C</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDuration(cycle.duration)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDuration(cycle.loadingTime)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDuration(cycle.unloadingTime)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex flex-wrap gap-1">
                    {cycle.compatibleResources.map(id => {
                      const resource = resources.find(r => r.id === id);
                      return resource ? (
                        <span key={id} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {resource.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex space-x-2">
                    <button 
                      className="text-blue-600 hover:text-blue-800"
                      onClick={() => {
                        setEditingCycle({...cycle});
                        setShowCycleModal(true);
                      }}
                    >
                      <Edit size={18} />
                    </button>
                    <button className="text-red-600 hover:text-red-800">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
  
  const ResourcesTab = () => (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-gray-800">Configuration des Moyens Chauds</h1>
        <button 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
          onClick={() => {
            setEditingResource({
              id: Math.max(...resources.map(r => r.id)) + 1,
              name: 'Nouveau Moyen',
              type: 'autoclave',
              capacity: 'Medium',
              maxTemp: 180,
              maxVacuumPorts: 4,
              maxTc: 8
            });
            setShowResourceModal(true);
          }}
        >
          <Plus size={18} className="mr-2" />
          Nouveau Moyen
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {resources.map((resource) => (
          <div key={resource.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">{resource.name}</h2>
              <div className="flex space-x-2">
                <button 
                  className="text-blue-600 hover:text-blue-800"
                  onClick={() => {
                    setEditingResource({...resource});
                    setShowResourceModal(true);
                  }}
                >
                  <Edit size={18} />
                </button>
                <button className="text-red-600 hover:text-red-800">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
            <div className="p-6">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-6">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">{resource.type}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Capacité</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.capacity}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Température Max</dt>
                  <dd className="mt-1 text-sm text-gray-900">{resource.maxTemp}°C</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Cycles compatibles</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {cycles
                      .filter(cycle => cycle.compatibleResources.includes(resource.id))
                      .map(cycle => cycle.name)
                      .join(', ')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nombre max. de prises de vide</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" className="mr-1 text-purple-600">
                      <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2"></circle>
                      <path d="M8 12h8" strokeWidth="2"></path>
                      <path d="M12 8v8" strokeWidth="2"></path>
                    </svg>
                    {resource.maxVacuumPorts}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Nombre max. de thermocouples</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" className="mr-1 text-red-600">
                      <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" fill="none" strokeWidth="2"></path>
                    </svg>
                    {resource.maxTc}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  
  const CycleModal = () => {
    if (!editingCycle) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-screen overflow-auto">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-bold text-gray-800">
              {editingCycle.id ? `Modifier: ${editingCycle.name}` : 'Nouveau Cycle'}
            </h2>
            <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowCycleModal(false)}>
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code du cycle
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingCycle.name}
                    onChange={(e) => setEditingCycle({...editingCycle, name: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <div className="flex space-x-3">
                  <button 
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowResourceModal(false)}
                  >
                    Annuler
                  </button>
                  <button 
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    onClick={() => setShowResourceModal(false)}
                  >
                    <Save size={18} className="mr-2" />
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-blue-800 text-gray-800 ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-1 md:space-x-10">
              <img src="/logo_bleu.png" alt="Logo" className="h-14 mr-3" />   
              <h1 className="text-xl font-bold text-white">
                Configuration des Moyens Chauds
              </h1>
            <div className="flex items-center space-x-4">
              {/* Bouton Navigation vers vue Production */}
              <Link to="/" className="p-2 text-white hover:text-gray-800 hover:bg-gray-100 rounded-full" title="Vue Production">
                <Home size={20} />
              </Link>
              
              {/* Bouton Navigation vers vue Manager Planning */}
              <Link to="/manager" className="p-2 text-white hover:text-gray-800 hover:bg-gray-100 rounded-full" title="Vue Planning">
                <ArrowLeft size={20} />
              </Link>
              
              <div className="flex border rounded-md p-1 bg-gray-500">
                <button 
                  className={`px-3 py-1 rounded-md ${activeTab === 'cycles' ? 'bg-white shadow-sm' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('cycles')}
                >
                  <div className="flex items-center">
                    <Settings size={16} className="mr-1" />
                    <span>Cycles</span>
                  </div>
                </button>
                <button 
                  className={`px-3 py-1 rounded-md ${activeTab === 'resources' ? 'bg-white shadow-sm' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('resources')}
                >
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    <span>Moyens</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'cycles' ? <CyclesTab /> : <ResourcesTab />}
      </main>
      
      {showCycleModal && <CycleModal />}
      {showResourceModal && <ResourceModal />}
      <footer className="bg-gray-100 border-t py-2 px-4 text-center text-xs text-gray-500">
        © 2025 PolyPlan - Application de planification des moyens chauds | Éditeur : KaiDaTa
      </footer>
    </div>
  );

  const ResourceModal = () => {
    if (!editingResource) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-screen overflow-auto">
          <div className="px-6 py-4 border-b flex justify-between items-center">
            <h2 className="text-lg font-bold text-gray-800">
              {editingResource.id ? `Modifier: ${editingResource.name}` : 'Nouveau Moyen Chaud'}
            </h2>
            <button className="text-gray-500 hover:text-gray-700" onClick={() => setShowResourceModal(false)}>
              <X size={24} />
            </button>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du moyen
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  value={editingResource.name}
                  onChange={(e) => setEditingResource({...editingResource, name: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingResource.type}
                    onChange={(e) => setEditingResource({...editingResource, type: e.target.value})}
                  >
                    <option value="autoclave">Autoclave</option>
                    <option value="etuve">Étuve</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacité
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingResource.capacity}
                    onChange={(e) => setEditingResource({...editingResource, capacity: e.target.value})}
                  >
                    <option value="Small">Petite</option>
                    <option value="Medium">Moyenne</option>
                    <option value="Large">Grande</option>
                  </select>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Température max (°C)
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingResource.maxTemp}
                    onChange={(e) => setEditingResource({...editingResource, maxTemp: parseInt(e.target.value)})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre max. de prises de vide
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingResource.maxVacuumPorts}
                    onChange={(e) => setEditingResource({...editingResource, maxVacuumPorts: parseInt(e.target.value)})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre max. de thermocouples
                  </label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    value={editingResource.maxTc}
                    onChange={(e) => setEditingResource({...editingResource, maxTc: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              
              <div className="flex justify-end pt-4 border-t">
                <div className="flex space-x-3">
                  <button 
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                    onClick={() => setShowResourceModal(false)}
                  >
                    Annuler
                  </button>
                  <button 
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    onClick={() => setShowResourceModal(false)}
                  >
                    <Save size={18} className="mr-2" />
                    Enregistrer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 md:space-x-10">
            <div className="flex justify-start lg:w-0 lg:flex-1">
              <h1 className="text-xl font-bold text-blue-800">
                Configuration des Moyens Chauds
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              {/* Bouton Navigation vers vue Production */}
              <Link to="/" className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full" title="Vue Production">
                <Home size={20} />
              </Link>
              
              {/* Bouton Navigation vers vue Manager Planning */}
              <Link to="/manager" className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full" title="Vue Planning">
                <ArrowLeft size={20} />
              </Link>
              
              <div className="flex border rounded-md p-1 bg-gray-50">
                <button 
                  className={`px-3 py-1 rounded-md ${activeTab === 'cycles' ? 'bg-white shadow-sm' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('cycles')}
                >
                  <div className="flex items-center">
                    <Settings size={16} className="mr-1" />
                    <span>Cycles</span>
                  </div>
                </button>
                <button 
                  className={`px-3 py-1 rounded-md ${activeTab === 'resources' ? 'bg-white shadow-sm' : 'hover:bg-gray-100'}`}
                  onClick={() => setActiveTab('resources')}
                >
                  <div className="flex items-center">
                    <Clock size={16} className="mr-1" />
                    <span>Moyens</span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {activeTab === 'cycles' ? <CyclesTab /> : <ResourcesTab />}
      </main>
      
      {showCycleModal && <CycleModal />}
      {showResourceModal && <ResourceModal />}
      <footer className="bg-gray-100 border-t py-2 px-4 text-center text-xs text-gray-500">
        © 2025 PolyPlan - Application de planification des moyens chauds | Éditeur : KaiDaTa
      </footer>
    </div>
  );
};


export default ConfigurationView;